import { Button, Col, Dropdown, Row, Spin, Table } from "antd";
import { StyleSheet } from "../../../StyleSheet";
import { useCallback, useEffect, useState } from "react";
import { CatalogBankResponse, DeleteGroupsRequest, GroupResponse, Status } from "../../../types/types";
import { EditOutlined, CloudDownloadOutlined, DeleteOutlined, LoadingOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import { useNavigate } from "react-router-dom";
import Search from "antd/es/input/Search";
import { deleteAccountGroupList, getAccountGroups, getBanks } from "../../../methods/axiosMethods";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { dispatch } from "../../../store";
import ModalConfirmation from "../../../components/ModalConfirmation";
import _ from "lodash";
import { MenuInfo } from "rc-menu/lib/interface";
import * as XLSX from 'xlsx';
import { setInstitution } from "../../../store/slices/institutions";
import { subMenusOfAccounts } from "../../../commons/subMenus";
import SubDefaultLayout from "../../../components/SubDefaultLayout";
import CardDashboard from "../../../components/CardDashboard";
import { image } from "../../../commons/ImageRoutes";
import Buttons from "../../../components/Buttons";
import ModalNotification, { AlertTypeNew } from "../../../components/ModalNotification";
import CustomPagination from "../../../components/CustomPagination";

const AccountsGroupMain = () => {
    const [accountGroupsList, setAccountGroupsList] = useState<Array<GroupResponse>>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const hasSelected = selectedRowKeys.length > 0;
    const banksList = useSelector((state: any) => state.institutions.institutionsInfo);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [showNotification, setShowNotification] = useState<boolean>(false);
    const [notificationMessage, setNotificationMessage] = useState("");
    const [notificationIcon, setNotificationIcon] = useState<AlertTypeNew>("SUCCESS");
    const [bankList, setBankList] = useState<CatalogBankResponse[]>([]);
    const [totalPerPage, setTotalPerPage] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchWord, setSearchWord] = useState("");

    const navigate = useNavigate();

    useEffect(() => {
        setCurrentPage(1);
    }, [totalPerPage]);

    const fetchData = useCallback(
        (page?: number, pageChanged?: number, searchTerm?: string) => {

            setLoading(true);

            const currentPageTmp = page ? page - 1 : 0;
            const currentPageChangedTmp = pageChanged ?? 10;
            const searchTermTmp = searchTerm ? searchTerm : "";

            getAccountGroups(currentPageTmp, currentPageChangedTmp, 'name,asc', searchTermTmp)
                .then((response) => {
                    const { data } = response;
                    if (data?.status === Status.OK && data?.data?.content) {
                        const groupList = data.data.content;
                        setAccountGroupsList(groupList);
                        setTotalRecords(data.data.totalElements);
                    } else {
                        setAccountGroupsList([]);
                        setTotalRecords(0);
                    }
                })
                .catch(() => {
                    setAccountGroupsList([]);
                    setTotalRecords(0);
                    setNotificationIcon("ERROR");
                    setNotificationMessage("Hubo un error al cargar la lista grupos de cuentas, intente más tarde.");
                    setShowNotification(true);
                })
                .finally(() => {
                    setLoading(false);
                });
        },
        []
    );

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        fetchData(page, totalPerPage, searchWord);
    };

    const handleTotalPerPage = (total: number) => {
        setTotalPerPage(total);
        fetchData(1, total, searchWord);
    };

    const handleSearch = async (text: string) => {
        setSearchWord(text);
        fetchData(1, totalPerPage, text);
    };

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const exportOpt = [
        { key: 'immediately', label: 'Inmediata' },
        { key: 'scheduled', label: 'Programada' },
    ];

    const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    const columns: ColumnsType<GroupResponse> = [
        {
            title: "Acciones",
            dataIndex: "operation",
            key: "operation",
            className: 'custom-header',
            render: (event, record: GroupResponse) => (
                <Row gutter={[16, 12]} justify={"center"}>
                    <Col>
                        <Dropdown
                            menu={{
                                onClick: (event) => exportToXLSX(event, record),
                                items: exportOpt,
                            }}
                            placement="bottom"
                            arrow={{ pointAtCenter: true }}
                        >
                            <Col>
                                <Button
                                    type="primary"
                                    shape='round'
                                    style={styles.textButtonExport}
                                >
                                    <span>
                                        Exportar
                                    </span>
                                    {<CloudDownloadOutlined />}
                                </Button>
                            </Col>
                        </Dropdown>
                    </Col>
                    <Col>
                        <Buttons
                            type="default"
                            title="Editar"
                            color="#ffb13d"
                            action={() => navigate(`/admin/accountsGroups/${record.id}`)}
                            icon={<EditOutlined />}
                        />
                    </Col>
                </Row>
            ),
        },
        {
            title: "Nombre",
            dataIndex: "name",
            key: "name",
            className: 'custom-header',
        },
        {
            title: "Descripción",
            dataIndex: "description",
            key: "description",
            className: 'custom-header',
        },
        {
            title: 'Creación',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (text) => dayjs(text).format("DD/MM/YYYY"),
            className: 'custom-header',
        },
        {
            title: "# de Cuentas",
            dataIndex: "thirdAccounts",
            key: "thirdAccounts",
            className: 'custom-header',
            align: "center",
            render: (accounts) => accounts.length,
        },
    ];

    useEffect(() => {
        if (banksList && _.isEmpty(banksList)) {
            setLoading(true);
            getBanks()
                .then((response) => {
                    if (response.data.status === Status.OK && response.data.data && !_.isEmpty(response.data.data)) {
                        dispatch(setInstitution({ institutionsInfo: response.data.data }))
                    }
                    setLoading(false);
                })
                .catch(() => {
                    setLoading(false);
                    setShowNotification(true);
                    setNotificationIcon("ERROR");
                    setNotificationMessage("Hubo un error al cargar la lista de Instituciones, intente más tarde.");
                });
        } else {
            setBankList(banksList);
        }
    }, [banksList]);

    const handleDeleteGroup = () => {
        setConfirmDelete(true);
    };

    const deleteGroup = () => {
        const data: DeleteGroupsRequest = { accountsGroup: [] };
        selectedRowKeys.forEach((element) => {
            if (element.valueOf()) {
                data.accountsGroup.push(element.toString());
            }
        })

        setConfirmDelete(false);
        setSelectedRowKeys([]);
        setLoading(true);
        deleteAccountGroupList(data)
            .then((response) => {
                if (response.data && response.data.status) {
                    setShowNotification(true);
                    setNotificationMessage(response.data.message);
                    if (response.data && response.data.status &&
                        response.data.status === Status.DELETED) {
                        setNotificationIcon("SUCCESS");
                    } else {
                        setNotificationIcon("ERROR");
                    }
                }
                setLoading(false);
                fetchData(currentPage, totalPerPage, searchWord);
            })
            .catch(() => {
                setLoading(false);
                setShowNotification(true);
                setNotificationMessage("Hubo un error borrando los grupos de cuentas");
                fetchData(currentPage, totalPerPage);
            })
    };

    // Funcion para crear la exportación a XLSX
    const exportToXLSX = (templateType: MenuInfo, groupAccounts: GroupResponse) => {
        const data: DeleteGroupsRequest = { accountsGroup: [] };
        selectedRowKeys.forEach((element) => {
            if (element.valueOf()) {
                data.accountsGroup.push(element.toString());
            }
        })
        const arrayToXLSX: any = [];
        const Heading = [
            [
                'Cuenta origen',
                'Cuenta destino CLABE/Tarjeta/Teléfono',
                'Tipo',
                'Nombre',
                'RFC',
                'Entidad',
                // estos datos los va a poner el usuario
                'Concepto',
                'Referencia numérica',
                'Importe Sin IVA',
                'IVA',
                _.isEqual(templateType.key, "scheduled") ? "Fecha (DD/MM/AAAA)" : null,
                _.isEqual(templateType.key, "scheduled") ? "Hora (HH:MM)" : null,
            ]
        ];

        groupAccounts.thirdAccounts.forEach(async element => {
            let accountTypeFormated = _.isEqual(element.accountType.toString(), "DEBIT") ? "Tarjeta de Debito"
                : _.isEqual(element.accountType.toString(), "CEL") ? "Teléfono celular"
                    : "CLABE"

            const objToDefine = {
                foreignAccountNumber: '',
                typeAccount: '',
                name: '',
                foreignBankId: '',
                rfc: ''
            };
            objToDefine.foreignAccountNumber = element.bankAccountNumber;
            objToDefine.typeAccount = accountTypeFormated;
            objToDefine.name = element.name;
            objToDefine.rfc = element.rfc;
            let dataResp = _.find(bankList, { bankCode: element.bankName });
            objToDefine.foreignBankId = dataResp ? dataResp.bankName : "";

            const objToOrder = Object.assign(objToDefine, element);
            arrayToXLSX.push({
                accountNumber: "'".toString(),
                foreignAccountNumber: objToOrder.foreignAccountNumber,
                typeAccount: objToOrder.typeAccount,
                name: objToOrder.name,
                rfc: objToOrder.rfc,
                foreignBankId: objToOrder.foreignBankId,
            });

        });
        const ws = XLSX.utils.book_new();
        const wb = XLSX.utils.book_new();
        const exportFileName = _.isEqual(templateType.key, "scheduled") ? 'Plantilla_programadas.xlsx' : "Plantilla_inmediatas.xlsx";

        XLSX.utils.sheet_add_aoa(ws, Heading);
        XLSX.utils.sheet_add_json(ws, arrayToXLSX, { origin: 'A2', skipHeader: true });
        XLSX.utils.book_append_sheet(wb, ws, 'Hoja1');
        const sheet: XLSX.Sheet = wb.Sheets['Hoja1'];
        sheet['!cols'] = [{ width: 25 }, { width: 35 }, {}, { width: 20 }, {}, {}, {}, { width: 20 }, { width: 20 }, { width: 20 }, { width: 20 }, { width: 20 }]; // Ajusta los valores de las columnas

        XLSX.writeFile(wb, exportFileName);
    };

    return (
        <SubDefaultLayout childrenSubMenu={subMenusOfAccounts} title="Grupos de Cuentas">
            <Row gutter={[15, 25]}>
                <Col span={24}>
                    <Row>
                        <Col span={24} md={6} lg={4}>
                            <CardDashboard
                                title={"Crear Grupo de Cuentas"}
                                icon={image.accountGroupsIcon}
                                action={"/admin/accountsGroups/add-group"}
                                height={135} />
                        </Col>
                    </Row>
                </Col>
                <Col span={24}>
                    <div style={styles.primaryContainer}>
                        <Row gutter={[16, 12]} style={styles.rowContainer}>
                            <Col xs={24} sm={24} md={12} lg={8} xl={8} xxl={5}>
                                <Search
                                    onSearch={(value: string) => handleSearch(value)}
                                    onChange={(value) =>
                                        _.isEmpty(value.target.value)
                                            ? (
                                                setSearchWord(""),
                                                fetchData(0, totalPerPage),
                                                setCurrentPage(1)
                                            )
                                            : null
                                    }
                                    placeholder="Buscar por nombre o descripción"
                                />
                            </Col>
                            <Col xs={0} sm={0} md={0} lg={8} xl={8} xxl={14} />
                            <Col
                                xs={24}
                                sm={24}
                                md={12}
                                lg={8}
                                xl={8}
                                xxl={5}
                                style={{ display: 'flex', justifyContent: 'space-evenly' }}
                            >
                                <Buttons
                                    type="primary"
                                    title="Borrar"
                                    color="#765CE9"
                                    isDisabled={!hasSelected}
                                    action={handleDeleteGroup}
                                    icon={<DeleteOutlined />}
                                    iconLeft
                                />
                            </Col>
                        </Row>
                        <Col xs={24} xl={24}>
                            <Table
                                dataSource={accountGroupsList}
                                columns={columns}
                                locale={{ emptyText: "No hay datos disponibles" }}
                                rowSelection={rowSelection}
                                rowKey={(record) => record.id}
                                scroll={{ y: 400, x: 1300 }}
                                pagination={false}
                                loading={{
                                    spinning: loading,
                                    indicator: <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />,
                                }}
                            />
                        </Col>
                        <Col xs={24}>
                            <CustomPagination
                                currentPage={currentPage}
                                pageSize={totalPerPage}
                                paginationTotal={totalRecords}
                                onPageSizeChange={handleTotalPerPage}
                                onPageChange={handlePageChange}
                                showSizeChanger
                            />
                        </Col>
                    </div>
                </Col>
            </Row>
            <ModalConfirmation
                open={confirmDelete}
                handleConfirm={deleteGroup}
                handleClose={() => setConfirmDelete(false)}
                message={"¿Estas seguro que deseas eliminar los grupos de cuentas seleccionados?"}
            />
            <ModalNotification
                title={"Grupos de Cuentas"}
                message={notificationMessage}
                alertType={notificationIcon}
                show={showNotification}
                onClose={() => setShowNotification(false)}
            />
        </SubDefaultLayout>
    );
};

const styles = StyleSheet.create({
    primaryContainer: {
        width: "100%",
        margin: "20px auto",
        padding: "20px",
        borderRadius: 10,
        backgroundColor: 'white',
        boxShadow: "rgba(177, 179, 185, 0.51) 5px 8px 24px 5px",
    },
    textButton: {
        color: '#ffffff',
        backgroundColor: '#1890ff',
        fontSize: 15
    },
    rowContainer: {
        marginBottom: '20px',
    },
    textButtonExport: {
        color: 'white',
        borderColor: '#3d3dff',
        backgroundColor: "#3d3dff"
    },
});

export default AccountsGroupMain;
